import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import type {
  MakeupCancellationRequest,
  MakeupRequest,
  RejectMakeupChangeRequest,
  RejectMakeupRequest,
  MakeupChangeRequest,
} from "@/openapi/generated";
import {
  createMakeupSession,
  deleteMakeupSession,
  getMakeupSession,
} from "@/lib/services/makeupService";
import {
  acceptMakeupChangeAction,
  approveMakeupSessionAction,
  cancelMakeupSessionAction,
  rejectMakeupChangeAction,
  rejectMakeupSessionAction,
  requestMakeupChangeAction,
} from "@/lib/makeups/makeup-session-actions";
import { invalidateMakeupQueries } from "@/features/makeups/shared/invalidateMakeupQueries";
import { upsertMakeupSessionInQueryCache } from "@/features/makeups/shared/upsertMakeupSessionInQueryCache";
import { QueryKeys } from "@/lib/types";

export function useMakeupMutations() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await invalidateMakeupQueries(queryClient);
  };

  const create = useMutation({
    mutationFn: async (makeupRequest: MakeupRequest) => {
      if (!accessToken) throw new Error("Authentication token is required");
      const result = await createMakeupSession(accessToken, makeupRequest);
      if (!result.isSuccess || !result.data?.id) {
        throw new Error(
          result.error?.message ?? "Could not create makeup request",
        );
      }
      return result;
    },
    onSuccess: async (result) => {
      const createdId = result.data?.id;
      if (createdId && accessToken) {
        try {
          const session = await getMakeupSession(
            accessToken,
            String(createdId),
          );
          upsertMakeupSessionInQueryCache(queryClient, session);
        } catch {
          await invalidateMakeupQueries(queryClient);
          return;
        }
      }
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.WidgetData],
      });
    },
  });

  const approve = useMutation({
    mutationFn: async (id: string) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return approveMakeupSessionAction(accessToken, id);
    },
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: RejectMakeupRequest;
    }) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return rejectMakeupSessionAction(accessToken, id, body);
    },
    onSuccess: invalidate,
  });

  const requestChange = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: MakeupChangeRequest;
    }) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return requestMakeupChangeAction(accessToken, id, body);
    },
    onSuccess: invalidate,
  });

  const acceptChange = useMutation({
    mutationFn: async (id: string) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return acceptMakeupChangeAction(accessToken, id);
    },
    onSuccess: invalidate,
  });

  const rejectChange = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: RejectMakeupChangeRequest;
    }) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return rejectMakeupChangeAction(accessToken, id, body);
    },
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: MakeupCancellationRequest;
    }) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return cancelMakeupSessionAction(accessToken, id, body);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!accessToken) throw new Error("Authentication token is required");
      return deleteMakeupSession(accessToken, id);
    },
    onSuccess: invalidate,
  });

  return {
    create,
    approve,
    reject,
    requestChange,
    acceptChange,
    rejectChange,
    cancel,
    remove,
    isMutating:
      create.isPending ||
      approve.isPending ||
      reject.isPending ||
      requestChange.isPending ||
      acceptChange.isPending ||
      rejectChange.isPending ||
      cancel.isPending ||
      remove.isPending,
  };
}
